import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  CheckCircle2, 
  TrendingDown, 
  Zap, 
  Database, 
  ShoppingCart, 
  BarChart3, 
  Workflow,
  Mail,
  Phone,
  MapPin,
  Calculator,
  Users,
  Lightbulb,
  Target,
  Clock,
  Search,
  Bot
} from 'lucide-react';
import { toast } from 'sonner';

const LandingPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Frontend-only validation
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus.');
      setIsSubmitting(false);
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Bitte geben Sie eine gültige E-Mail-Adresse ein.');
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast.success('Danke! Ich melde mich innerhalb von 24 Stunden.', {
          description: 'Ihre Anfrage wurde erfolgreich versendet.'
        });
        setFormData({ name: '', email: '', company: '', message: '' });
        
        // Google Ads Conversion Tracking
        if (typeof window.gtag_report_conversion === 'function') {
          window.gtag_report_conversion();
        }
      } else {
        throw new Error('Fehler beim Versenden');
      }
    } catch (error) {
      toast.error('Fehler beim Versenden der Nachricht.', {
        description: 'Bitte versuchen Sie es später erneut.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToContact = () => {
    document.getElementById('kontakt')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="landing-page">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/95 backdrop-blur-sm shadow-sm z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <Target className="h-6 w-6 text-primary" />
              <span className="font-bold text-xl">Daniel Leismann</span>
            </div>
            <div className="hidden md:flex space-x-8">
              <a href="#leistungen" className="nav-link">Leistungen</a>
              <a href="#geschaeftsmodell" className="nav-link">Geschäftsmodell</a>
              <a href="#ueber-mich" className="nav-link">Über mich</a>
              <a href="#ablauf" className="nav-link">Ablauf</a>
              <a href="#kontakt" className="nav-link">Kontakt</a>
              <Link to="/erstgespraech" className="nav-link font-semibold text-accent">Termin buchen</Link>
            </div>
            <Link to="/erstgespraech">
              <Button className="cta-button">
                Termin buchen
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-[1.2fr,0.8fr] gap-20 items-start">
            <div className="hero-content" style={{maxWidth: '100%'}}>
              <h1 className="hero-title" style={{color: '#0f172a'}}>
                Digitale Prozesse optimieren. Kosten nachhaltig senken.
              </h1>
              <p className="hero-subtitle">
                Buchhaltung, Schnittstellen, Workflows und Online-Vertrieb vereinfachen – durch Automatisierung, KI und klare Datenflüsse. Vergütung nur aus realen Einsparungen.
              </p>
              
              {/* Bild über den Buttons */}
              <div className="hero-image-mobile my-8">
                <div className="hero-image-wrapper" style={{padding: '20px'}}>
                  <img 
                    src="https://customer-assets.emergentagent.com/job_leismann-digital/artifacts/52t5d2l5_daniel_leismann.jpg" 
                    alt="Daniel Leismann" 
                    className="hero-profile-image"
                  />
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 mt-8">
                <Link to="/erstgespraech">
                  <Button size="lg" className="cta-button-large">
                    Termin jetzt buchen
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Button variant="outline" size="lg" onClick={scrollToContact}>
                  Oder Kontaktformular nutzen
                </Button>
              </div>
              <div className="mt-6 text-center">
                <p className="text-lg font-semibold text-primary">Transparent. Messbar. Ohne Risiko.</p>
              </div>
              <div className="mt-6 flex flex-wrap justify-center gap-6 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-accent" />
                  <span>Keine Vorkosten</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-accent" />
                  <span>Kein Risiko</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-accent" />
                  <span>Messbare Ergebnisse</span>
                </div>
              </div>
            </div>
            <div className="hero-image-container hero-image-desktop">
              <div className="hero-image-wrapper" style={{padding: '20px'}}>
                <img 
                  src="https://customer-assets.emergentagent.com/job_leismann-digital/artifacts/52t5d2l5_daniel_leismann.jpg" 
                  alt="Daniel Leismann" 
                  className="hero-profile-image"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Leistungen Section */}
      <section id="leistungen" className="section-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="section-header">
            <h2 className="section-title">Was ich für Sie optimiere</h2>
            <p className="section-subtitle">
              Von KI-Integration bis Multichannel-Vertrieb – ich identifiziere Engpässe und löse sie pragmatisch.
            </p>
          </div>

          <div className="services-grid">
            <Link to="/prozessautomatisierung" className="no-underline">
              <Card className="service-card">
                <CardContent className="p-6">
                  <div className="service-icon">
                    <Zap className="h-6 w-6" />
                  </div>
                  <h3 className="service-title">Prozessautomatisierung</h3>
                  <p className="service-description">
                    Workflows automatisieren, API-Integration, Schnittstellen programmieren.
                  </p>
                  <div className="mt-4 text-accent font-semibold flex items-center gap-2">
                    Mehr erfahren <ArrowRight className="h-4 w-4" />
                  </div>
                </CardContent>
              </Card>
            </Link>

            <Link to="/digitale-buchhaltung" className="no-underline">
              <Card className="service-card">
                <CardContent className="p-6">
                  <div className="service-icon">
                    <Database className="h-6 w-6" />
                  </div>
                  <h3 className="service-title">Digitale Buchhaltung</h3>
                  <p className="service-description">
                    LexOffice, DATEV, Addison - digitale Rechnungsstellung und Buchhaltung.
                  </p>
                  <div className="mt-4 text-accent font-semibold flex items-center gap-2">
                    Mehr erfahren <ArrowRight className="h-4 w-4" />
                  </div>
                </CardContent>
              </Card>
            </Link>

            <Link to="/amazon-optimierung" className="no-underline">
              <Card className="service-card">
                <CardContent className="p-6">
                  <div className="service-icon">
                    <ShoppingCart className="h-6 w-6" />
                  </div>
                  <h3 className="service-title">Amazon Optimierung</h3>
                  <p className="service-description">
                    Amazon SEO, VCS Lite, FBA-Logistik und Vendor Central optimieren.
                  </p>
                  <div className="mt-4 text-accent font-semibold flex items-center gap-2">
                    Mehr erfahren <ArrowRight className="h-4 w-4" />
                  </div>
                </CardContent>
              </Card>
            </Link>

            <Link to="/multichannel-vertrieb" className="no-underline">
              <Card className="service-card">
                <CardContent className="p-6">
                  <div className="service-icon">
                    <Workflow className="h-6 w-6" />
                  </div>
                  <h3 className="service-title">Multichannel-Vertrieb</h3>
                  <p className="service-description">
                    Amazon, eBay, Otto - alle Verkaufskanäle zentral verwalten und synchronisieren.
                  </p>
                  <div className="mt-4 text-accent font-semibold flex items-center gap-2">
                    Mehr erfahren <ArrowRight className="h-4 w-4" />
                  </div>
                </CardContent>
              </Card>
            </Link>

            <Link to="/seo-sea-optimierung" className="no-underline">
              <Card className="service-card">
                <CardContent className="p-6">
                  <div className="service-icon">
                    <BarChart3 className="h-6 w-6" />
                  </div>
                  <h3 className="service-title">SEO & SEA Optimierung</h3>
                  <p className="service-description">
                    Google Ads, Bing Ads, Suchmaschinenoptimierung für mehr Sichtbarkeit.
                  </p>
                  <div className="mt-4 text-accent font-semibold flex items-center gap-2">
                    Mehr erfahren <ArrowRight className="h-4 w-4" />
                  </div>
                </CardContent>
              </Card>
            </Link>

            <Link to="/preisoptimierung-angebotserstellung" className="no-underline">
              <Card className="service-card">
                <CardContent className="p-6">
                  <div className="service-icon">
                    <Calculator className="h-6 w-6" />
                  </div>
                  <h3 className="service-title">Preisoptimierung</h3>
                  <p className="service-description">
                    EK/VK-Kalkulation, Preisformeln, dynamische Preisanpassung optimieren.
                  </p>
                  <div className="mt-4 text-accent font-semibold flex items-center gap-2">
                    Mehr erfahren <ArrowRight className="h-4 w-4" />
                  </div>
                </CardContent>
              </Card>
            </Link>

            <Link to="/lagerverwaltung-logistik" className="no-underline">
              <Card className="service-card">
                <CardContent className="p-6">
                  <div className="service-icon">
                    <Database className="h-6 w-6" />
                  </div>
                  <h3 className="service-title">Lagerverwaltung & Logistik</h3>
                  <p className="service-description">
                    Warenwirtschaft, Bestandsmanagement, Versandlogistik automatisieren.
                  </p>
                  <div className="mt-4 text-accent font-semibold flex items-center gap-2">
                    Mehr erfahren <ArrowRight className="h-4 w-4" />
                  </div>
                </CardContent>
              </Card>
            </Link>

            <Link to="/steuer-compliance-oss" className="no-underline">
              <Card className="service-card">
                <CardContent className="p-6">
                  <div className="service-icon">
                    <Database className="h-6 w-6" />
                  </div>
                  <h3 className="service-title">Steuer-Compliance & OSS</h3>
                  <p className="service-description">
                    OSS Schweiz, MOSS, Umsatzsteuer - grenzüberschreitend verkaufen.
                  </p>
                  <div className="mt-4 text-accent font-semibold flex items-center gap-2">
                    Mehr erfahren <ArrowRight className="h-4 w-4" />
                  </div>
                </CardContent>
              </Card>
            </Link>

            <Link to="/ki-werbung-social-media" className="no-underline">
              <Card className="service-card">
                <CardContent className="p-6">
                  <div className="service-icon">
                    <BarChart3 className="h-6 w-6" />
                  </div>
                  <h3 className="service-title">KI-Werbung & Social Media</h3>
                  <p className="service-description">
                    Facebook Ads, Instagram, TikTok - KI-gestützte Werbekampagnen.
                  </p>
                  <div className="mt-4 text-accent font-semibold flex items-center gap-2">
                    Mehr erfahren <ArrowRight className="h-4 w-4" />
                  </div>
                </CardContent>
              </Card>
            </Link>

            <Link to="/influencer-marketing-akquise" className="no-underline">
              <Card className="service-card">
                <CardContent className="p-6">
                  <div className="service-icon">
                    <Users className="h-6 w-6" />
                  </div>
                  <h3 className="service-title">Influencer Marketing</h3>
                  <p className="service-description">
                    Influencer-Recherche, Akquise und Kampagnen-Management.
                  </p>
                  <div className="mt-4 text-accent font-semibold flex items-center gap-2">
                    Mehr erfahren <ArrowRight className="h-4 w-4" />
                  </div>
                </CardContent>
              </Card>
            </Link>

            <Link to="/ki-kundenservice" className="no-underline">
              <Card className="service-card">
                <CardContent className="p-6">
                  <div className="service-icon">
                    <Users className="h-6 w-6" />
                  </div>
                  <h3 className="service-title">KI-Kundenservice</h3>
                  <p className="service-description">
                    Chatbots, automatisierte Antworten, 24/7 Support mit KI.
                  </p>
                  <div className="mt-4 text-accent font-semibold flex items-center gap-2">
                    Mehr erfahren <ArrowRight className="h-4 w-4" />
                  </div>
                </CardContent>
              </Card>
            </Link>

            <Link to="/gpsr-produktsicherheit" className="no-underline">
              <Card className="service-card">
                <CardContent className="p-6">
                  <div className="service-icon">
                    <Database className="h-6 w-6" />
                  </div>
                  <h3 className="service-title">GPSR & Produktsicherheit</h3>
                  <p className="service-description">
                    EU-Produktsicherheitsverordnung, Compliance ab Dezember 2024.
                  </p>
                  <div className="mt-4 text-accent font-semibold flex items-center gap-2">
                    Mehr erfahren <ArrowRight className="h-4 w-4" />
                  </div>
                </CardContent>
              </Card>
            </Link>

            <Link to="/custom-software-dashboards" className="no-underline">
              <Card className="service-card">
                <CardContent className="p-6">
                  <div className="service-icon">
                    <Zap className="h-6 w-6" />
                  </div>
                  <h3 className="service-title">Custom Software & Dashboards</h3>
                  <p className="service-description">
                    Eigene Tools, Custom GPTs, Python-Programmierung, individuelle Dashboards.
                  </p>
                  <div className="mt-4 text-accent font-semibold flex items-center gap-2">
                    Mehr erfahren <ArrowRight className="h-4 w-4" />
                  </div>
                </CardContent>
              </Card>
            </Link>

            <Link to="/website-crawler" className="no-underline">
              <Card className="service-card">
                <CardContent className="p-6">
                  <div className="service-icon">
                    <Search className="h-6 w-6" />
                  </div>
                  <h3 className="service-title">Website Crawler</h3>
                  <p className="service-description">
                    Automatisierte Datenerfassung, Preisüberwachung, Wettbewerbsanalyse.
                  </p>
                  <div className="mt-4 text-accent font-semibold flex items-center gap-2">
                    Mehr erfahren <ArrowRight className="h-4 w-4" />
                  </div>
                </CardContent>
              </Card>
            </Link>

            <Link to="/ki-vertriebsagenten" className="no-underline">
              <Card className="service-card">
                <CardContent className="p-6">
                  <div className="service-icon">
                    <Bot className="h-6 w-6" />
                  </div>
                  <h3 className="service-title">KI Vertriebsagenten</h3>
                  <p className="service-description">
                    Automatisierte Lead-Qualifizierung, Sales-Automation, 24/7 Erstkontakt.
                  </p>
                  <div className="mt-4 text-accent font-semibold flex items-center gap-2">
                    Mehr erfahren <ArrowRight className="h-4 w-4" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>
      </section>

      {/* Visual Section with Image */}
      <section className="visual-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="section-title-dark">Analyse, die wirklich spart</h2>
              <p className="text-lg text-gray-300 mb-6">
                Mit jahrelanger Erfahrung in digitalem Vertrieb, Datenanalyse und Prozessoptimierung erkenne ich schnell, wo Ihr Unternehmen Geld verliert – und wie wir das ändern können.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="h-6 w-6 text-accent flex-shrink-0 mt-1" />
                  <span className="text-gray-300">Tiefenanalyse Ihrer aktuellen Prozesse und Systeme</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="h-6 w-6 text-accent flex-shrink-0 mt-1" />
                  <span className="text-gray-300">Identifikation konkreter Einsparpotenziale</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="h-6 w-6 text-accent flex-shrink-0 mt-1" />
                  <span className="text-gray-300">Umsetzung mit modernsten Tools und Methoden</span>
                </li>
              </ul>
            </div>
            <div className="visual-image-wrapper">
              <img 
                src="https://images.pexels.com/photos/669610/pexels-photo-669610.jpeg" 
                alt="Datenanalyse und Dashboard" 
                className="visual-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Geschäftsmodell Section */}
      <section id="geschaeftsmodell" className="section-light">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="section-header">
            <h2 className="section-title">Sie zahlen nur, wenn Sie wirklich sparen</h2>
            <p className="section-subtitle">
              Mein Geschäftsmodell ist einfach, transparent und fair: Keine Vorkosten, kein Risiko – nur Erfolgshonorar.
            </p>
          </div>

          <div className="business-model-card">
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bm-step">
                <div className="bm-number">1</div>
                <h3 className="bm-title">Kostenlose Erstberatung</h3>
                <p className="bm-description">
                  Wir analysieren gemeinsam Ihre Prozesse und identifizieren konkrete Einsparpotenziale.
                </p>
              </div>
              <div className="bm-step">
                <div className="bm-number">2</div>
                <h3 className="bm-title">Transparente Kalkulation</h3>
                <p className="bm-description">
                  Die erwarteten Einsparungen werden vorab berechnet und transparent dokumentiert.
                </p>
              </div>
              <div className="bm-step">
                <div className="bm-number">3</div>
                <h3 className="bm-title">Erfolgsbasierte Vergütung</h3>
                <p className="bm-description">
                  Sie zahlen nur einen vereinbarten Prozentsatz aus den tatsächlich realisierten Einsparungen.
                </p>
              </div>
            </div>

            <div className="bm-highlight">
              <TrendingDown className="h-12 w-12 text-accent mb-4" />
              <h3 className="text-2xl font-bold mb-2">Win-Win-Modell</h3>
              <p className="text-lg">
                Keine Fixkosten. Kein unternehmerisches Risiko. Ich verdiene nur, wenn Sie nachweislich sparen.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Typische Einsparungen Tabelle */}
      <section className="section-light" style={{paddingTop: '4rem'}}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="section-header">
            <h2 className="section-title">Wo Unternehmen typischerweise sparen</h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="savings-table">
              <thead>
                <tr>
                  <th>Bereich</th>
                  <th>Typische Einsparung</th>
                  <th>Beispiel</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>Digitale Buchhaltung</strong></td>
                  <td>20–60 % weniger Zeit</td>
                  <td>Automatisierte DATEV-/LexOffice-Exporte</td>
                </tr>
                <tr>
                  <td><strong>Amazon / eBay</strong></td>
                  <td>+10–30 % Marge</td>
                  <td>Optimierte Preisformeln, weniger Fehler</td>
                </tr>
                <tr>
                  <td><strong>Prozessautomatisierung</strong></td>
                  <td>30–90 % weniger Aufwand</td>
                  <td>Automatische Rechnungszuordnung</td>
                </tr>
                <tr>
                  <td><strong>Custom Dashboards</strong></td>
                  <td>Mehr Transparenz</td>
                  <td>Alle Kennzahlen an einem Ort</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Use-Case-Beispiele */}
      <section className="section-gradient">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="section-header">
            <h2 className="section-title-white">Echte Ergebnisse aus der Praxis</h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="bg-white">
              <CardContent className="p-6">
                <div className="text-4xl font-bold text-accent mb-4">12h</div>
                <h3 className="text-xl font-bold mb-3">Buchhaltung automatisiert</h3>
                <p className="text-gray-700">
                  12 Stunden pro Woche Buchhaltungsaufwand eingespart – durch automatisierte LexOffice-/DATEV-Flows.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white">
              <CardContent className="p-6">
                <div className="text-4xl font-bold text-accent mb-4">-70%</div>
                <h3 className="text-xl font-bold mb-3">Fehlerquote gesenkt</h3>
                <p className="text-gray-700">
                  Produktfehlerquote auf Amazon um 70 % gesenkt – durch saubere Daten und Automationen.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white">
              <CardContent className="p-6">
                <div className="text-4xl font-bold text-accent mb-4">-3</div>
                <h3 className="text-xl font-bold mb-3">Tools eingespart</h3>
                <p className="text-gray-700">
                  Intern 3 Tools abgeschafft – alles in einem Custom Dashboard vereint.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Über mich Section */}
      <section id="ueber-mich" className="section-about">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-5 gap-12 items-center">
            <div className="md:col-span-2">
              <div className="about-image-wrapper">
                <img 
                  src="https://customer-assets.emergentagent.com/job_leismann-digital/artifacts/52t5d2l5_daniel_leismann.jpg" 
                  alt="Daniel Leismann" 
                  className="about-profile-image"
                />
              </div>
            </div>
            <div className="md:col-span-3">
              <h2 className="section-title">Über mich</h2>
              <h3 className="text-2xl font-semibold mb-4 text-primary">Daniel Leismann</h3>
              <div className="about-text">
                <p className="mb-4">
                  Ich habe Mathematik und Sport fürs Gymnasiallehramt studiert und viele Jahre als Mathelehrer gearbeitet. Anschließend habe ich die Firma <a href="https://www.score-schleifwerkzeuge.de" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline font-semibold">SCORE Schleifwerkzeuge</a> aufgebaut – mit eigenem Onlineshop, Mitarbeitern, digitalem Vertrieb, automatisierten Prozessen und komplexem Datenmanagement. In dieser Rolle habe ich täglich mit großen Produktdatenmengen, Multichannel-Systemen und Prozessautomatisierung gearbeitet.
                </p>
                <p className="font-semibold text-lg text-primary">
                  Mein Ansatz ist radikal einfach: Sie zahlen nur, wenn Sie wirklich sparen. Kein Risiko, keine Luftnummer.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Image Section */}
      <section className="visual-section-alt">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="visual-image-wrapper">
              <img 
                src="https://images.unsplash.com/photo-1739298061740-5ed03045b280" 
                alt="Team Collaboration" 
                className="visual-image"
              />
            </div>
            <div>
              <h2 className="section-title-dark">Persönliche Betreuung, echte Ergebnisse</h2>
              <p className="text-lg text-gray-300 mb-6">
                Als Ihr direkter Ansprechpartner begleite ich Sie vom ersten Gespräch bis zur erfolgreichen Umsetzung. Keine anonyme Agentur, sondern persönliches Sparring auf Augenhöhe.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Users className="h-6 w-6 text-accent flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-white mb-1">Direkter Kontakt</h4>
                    <p className="text-gray-300">Kurze Wege, schnelle Entscheidungen, persönlicher Service</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Lightbulb className="h-6 w-6 text-accent flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-white mb-1">Praxisnahe Lösungen</h4>
                    <p className="text-gray-300">Keine Theorie, sondern bewährte Methoden aus der echten Welt</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Clock className="h-6 w-6 text-accent flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-white mb-1">Schnelle Umsetzung</h4>
                    <p className="text-gray-300">Keine langen Verträge oder endlosen Planungsphasen</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ablauf Section */}
      <section id="ablauf" className="section-light">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="section-header">
            <h2 className="section-title">So arbeiten wir zusammen</h2>
            <p className="section-subtitle">
              Von der Erstanalyse bis zur erfolgreichen Umsetzung – transparent und strukturiert.
            </p>
          </div>

          <div className="timeline">
            <div className="timeline-item">
              <div className="timeline-marker">1</div>
              <div className="timeline-content">
                <h3 className="timeline-title">Kostenloses Erstgespräch</h3>
                <p className="timeline-description">
                  Wir lernen uns kennen und besprechen Ihre aktuellen Herausforderungen. Ich verschaffe mir einen ersten Überblick über Ihre Prozesse und Systeme.
                </p>
              </div>
            </div>

            <div className="timeline-item">
              <div className="timeline-marker">2</div>
              <div className="timeline-content">
                <h3 className="timeline-title">Detailanalyse & Potenzialermittlung</h3>
                <p className="timeline-description">
                  Ich analysiere Ihre Workflows, Datenflüsse und Systeme im Detail. Gemeinsam identifizieren wir konkrete Einsparpotenziale und berechnen die erwarteten Einsparungen.
                </p>
              </div>
            </div>

            <div className="timeline-item">
              <div className="timeline-marker">3</div>
              <div className="timeline-content">
                <h3 className="timeline-title">Transparente Vereinbarung</h3>
                <p className="timeline-description">
                  Wir definieren klare Ziele und vereinbaren den Prozentsatz, den Sie aus den realisierten Einsparungen zahlen. Alles ist messbar und nachvollziehbar.
                </p>
              </div>
            </div>

            <div className="timeline-item">
              <div className="timeline-marker">4</div>
              <div className="timeline-content">
                <h3 className="timeline-title">Umsetzung & Optimierung</h3>
                <p className="timeline-description">
                  Ich implementiere die Lösungen, integriere Systeme und automatisiere Prozesse. Sie behalten jederzeit die volle Kontrolle und Transparenz.
                </p>
              </div>
            </div>

            <div className="timeline-item">
              <div className="timeline-marker">5</div>
              <div className="timeline-content">
                <h3 className="timeline-title">Erfolgskontrolle & Reporting</h3>
                <p className="timeline-description">
                  Wir messen gemeinsam die tatsächlichen Einsparungen. Sie sehen schwarz auf weiß, was optimiert wurde – und zahlen entsprechend der vereinbarten Erfolgsquote.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Zielgruppen Section */}
      <section className="section-gradient">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="section-title-white">Für wen ist das geeignet?</h2>
          <p className="section-subtitle-white">
            Meine Beratung richtet sich an Unternehmen, die digitale Effizienz ernst nehmen.
          </p>

          <div className="grid md:grid-cols-3 gap-8 mt-12">
            <div className="target-group-card">
              <div className="target-icon">
                <ShoppingCart className="h-8 w-8" />
              </div>
              <h3 className="target-title">E-Commerce & Onlinehändler</h3>
              <p className="target-description">
                Multichannel-Vertrieb, Plattform-Optimierung, Margenkalkulation und Prozessautomatisierung
              </p>
            </div>

            <div className="target-group-card">
              <div className="target-icon">
                <Database className="h-8 w-8" />
              </div>
              <h3 className="target-title">Mittelständische Unternehmen</h3>
              <p className="target-description">
                Digitalisierung der Buchhaltung, Datenintegration und effiziente Geschäftsprozesse
              </p>
            </div>

            <div className="target-group-card">
              <div className="target-icon">
                <BarChart3 className="h-8 w-8" />
              </div>
              <h3 className="target-title">Wachstumsunternehmen</h3>
              <p className="target-description">
                Skalierbare Systeme, Analytics, Reporting und datengetriebene Entscheidungsfindung
              </p>
            </div>
          </div>

          <div className="mt-12">
            <p className="text-lg text-gray-700 mb-6">
              <strong>Sie sind sich unsicher, ob ich der richtige Partner bin?</strong><br />
              Lassen Sie uns unverbindlich sprechen – ich höre zu und berate ehrlich.
            </p>
          </div>
        </div>
      </section>

      {/* Kontakt Section */}
      <section id="kontakt" className="section-contact">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="section-header">
            <h2 className="section-title">Bereit für echte Einsparungen?</h2>
            <p className="section-subtitle">
              Kontaktieren Sie mich für eine kostenlose Erstanalyse – ohne Verpflichtung, ohne Risiko.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h3 className="text-2xl font-bold mb-6">Kontaktinformationen</h3>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="contact-icon">
                    <Mail className="h-6 w-6" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">E-Mail</h4>
                    <a href="mailto:mail@daniel-leismann.de" className="contact-link">
                      mail@daniel-leismann.de
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="contact-icon">
                    <Phone className="h-6 w-6" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Telefon</h4>
                    <a href="tel:+4915253789753" className="contact-link">
                      0152-53789753
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="contact-icon">
                    <MapPin className="h-6 w-6" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Standort</h4>
                    <p className="text-muted-foreground">Köln-Nippes, Deutschland</p>
                  </div>
                </div>
              </div>

              <div className="mt-8 p-6 bg-muted rounded-lg">
                <h4 className="font-semibold mb-2 flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-accent" />
                  Schnelle Rückmeldung
                </h4>
                <p className="text-sm text-muted-foreground">
                  Ich antworte in der Regel innerhalb von 24 Stunden auf alle Anfragen.
                </p>
              </div>
            </div>

            <div>
              <Card>
                <CardContent className="p-6">
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <Label htmlFor="name">Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        placeholder="Ihr Name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label htmlFor="email">E-Mail *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="ihre@email.de"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label htmlFor="company">Firma (optional)</Label>
                      <Input
                        id="company"
                        name="company"
                        type="text"
                        placeholder="Ihr Unternehmen"
                        value={formData.company}
                        onChange={handleInputChange}
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label htmlFor="message">Wobei sollen wir sparen? *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        placeholder="Beschreiben Sie kurz, welche Prozesse Sie optimieren möchten oder wo Sie Einsparpotenzial sehen..."
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                        rows={6}
                        className="mt-1"
                      />
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full cta-button-large"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Wird gesendet...' : 'Kostenlose Erstanalyse anfragen'}
                      {!isSubmitting && <ArrowRight className="ml-2 h-5 w-5" />}
                    </Button>

                    <p className="text-xs text-muted-foreground text-center mt-4">
                      * Pflichtfelder. Ihre Daten werden vertraulich behandelt.
                    </p>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <Target className="h-6 w-6 text-accent" />
                <span className="font-bold text-lg">Daniel Leismann</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Digitale Prozessoptimierung & Automatisierung.<br />
                Bezahlung nur aus realen Einsparungen.
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Navigation</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#leistungen" className="footer-link">Leistungen</a></li>
                <li><a href="#geschaeftsmodell" className="footer-link">Geschäftsmodell</a></li>
                <li><a href="#ueber-mich" className="footer-link">Über mich</a></li>
                <li><a href="#ablauf" className="footer-link">Ablauf</a></li>
                <li><a href="#kontakt" className="footer-link">Kontakt</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Kontakt</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <a href="mailto:mail@daniel-leismann.de" className="footer-link">
                    mail@daniel-leismann.de
                  </a>
                </li>
                <li>
                  <a href="tel:+4915253789753" className="footer-link">
                    0152-53789753
                  </a>
                </li>
                <li className="text-muted-foreground">Köln-Nippes, Deutschland</li>
              </ul>
            </div>
          </div>

          <div className="border-t border-border pt-8 text-center text-sm text-muted-foreground">
            <p>&copy; {new Date().getFullYear()} Daniel Leismann. Alle Rechte vorbehalten.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;